/**
 * RRULE expansion wrapper — the ONLY file in the codebase that imports
 * `rrule` for task schedules (docs/plans/planned-task-schedule.md §2).
 *
 * rrule works in "fake UTC" JS Dates: a local calendar date is represented as
 * midnight UTC on that date. Local YYYY-MM-DD strings are converted on the way
 * in with Date.UTC and back out with toISOString().slice(0, 10), so no
 * timezone math happens anywhere — and a JS Date never escapes this module.
 *
 * Targets the rrule 2.6.4 API (jms-api pins 2.6.4, jms-web 2.8.1); nothing
 * newer than 2.6.4 is used.
 */

import { RRule } from 'rrule';

const MS_PER_DAY = 86400000;

/** Local YYYY-MM-DD → fake-UTC Date at midnight. */
function toFakeUtc(date: string): Date {

    const [year, month, day] = date.split('-').map(Number);

    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

/** Fake-UTC Date → local YYYY-MM-DD. */
function fromFakeUtc(date: Date): string {

    return date.toISOString().slice(0, 10);
}

function buildRule(scheduleRule: string, effectiveFrom: string): RRule {

    // rrule 2.6.4 wants DTSTART inside the string when parsing via fromString;
    // options parsing is used instead so DTSTART stays a fake-UTC instant.
    const options = RRule.parseString(scheduleRule);

    options.dtstart = toFakeUtc(effectiveFrom);
    options.wkst = RRule.MO;

    return new RRule(options);
}

/**
 * Raw pattern occurrences within [from, to] inclusive, as local date strings.
 * Nothing before effectiveFrom is ever produced (DTSTART clips it).
 */
export function expandRule(scheduleRule: string, effectiveFrom: string, from: string, to: string): string[] {

    if (from > to) {
        return [];
    }

    const windowStart = from < effectiveFrom ? effectiveFrom : from;

    if (windowStart > to) {
        return [];
    }

    const rule = buildRule(scheduleRule, effectiveFrom);
    const occurrences = rule.between(toFakeUtc(windowStart), toFakeUtc(to), true);

    return occurrences.map(fromFakeUtc);
}

/**
 * First pattern occurrence strictly after `date`, or undefined when the rule
 * is exhausted. Uses rrule's own iterator rather than expanding a window, so
 * a yearly rule costs one step, not a year of dates.
 */
export function nextRuleOccurrence(scheduleRule: string, effectiveFrom: string, date: string): string | undefined {

    const rule = buildRule(scheduleRule, effectiveFrom);
    const anchor = date < effectiveFrom ? addDaysFakeUtc(effectiveFrom, -1) : toFakeUtc(date);
    const next = rule.after(anchor, false);

    return next ? fromFakeUtc(next) : undefined;
}

/** Nth pattern occurrence (0-based) from DTSTART, or undefined when the rule is shorter. */
export function ruleOccurrenceAt(scheduleRule: string, effectiveFrom: string, index: number): string | undefined {

    const rule = buildRule(scheduleRule, effectiveFrom);
    let found: Date | undefined;

    rule.all((occurrence, length) => {

        if (length - 1 === index) {
            found = occurrence;

            return false;
        }

        return true;
    });

    return found ? fromFakeUtc(found) : undefined;
}

/** Fake-UTC day arithmetic, used only to build the exclusive anchor for `after`. */
function addDaysFakeUtc(date: string, days: number): Date {

    return new Date(toFakeUtc(date).getTime() + days * MS_PER_DAY);
}
